import { useEffect } from 'react';

import {
  type TActionInput,
  type TGroup,
  type TMode,
  type TModePath,
  type TType,
  useSpotlightAPI,
} from '@frontend/components/Spotlight/store';

/**
 * Register Spotlight actions, grouped by mode path, for the lifetime of the calling
 * component. Pass a stable (memoized or module-constant) reference — a new identity
 * re-registers.
 */
export function useSpotlightActions(actions: Record<TModePath, TActionInput[]>): void {
  const api = useSpotlightAPI();
  useEffect(() => api.addActions(actions), [api, actions]);
}

/** Register action render `types` (keyed by type) for the component's lifetime. Pass a stable reference. */
export function useSpotlightActionTypes(types: Record<string, TType>): void {
  const api = useSpotlightAPI();
  useEffect(() => api.registerType(types), [api, types]);
}

/** Register `groups` (keyed by group key) for the component's lifetime. Pass a stable reference. */
export function useSpotlightActionGroups(groups: Record<string, TGroup>): void {
  const api = useSpotlightAPI();
  useEffect(() => api.registerGroup(groups), [api, groups]);
}

/** Register `modes` (keyed by mode path) for the component's lifetime. Pass a stable reference. */
export function useSpotlightModes(modes: Record<TModePath, TMode>): void {
  const api = useSpotlightAPI();
  useEffect(() => api.registerModes(modes), [api, modes]);
}
